import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import type * as Domain from '~/domain';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import type { IToastMessage } from '~/helpers/Toastr';
import { toastr } from '~/helpers/Toastr';
import type { IAppState } from '~/appstate';
import * as Hub from '../ogm-hub';

export type IBlueprintsState = EntityState<Domain.IBlueprint>;
export const blueprintsAdapter = createEntityAdapter<Domain.IBlueprint>();

export const fetchBlueprint = createAsyncThunk<
	Domain.IBlueprint,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'blueprints/fetchBlueprint',
	async (blueprintId: number, { rejectWithValue }) => {
		try {
			const result = await unwrapFetch2(
				Hub.BlueprintController.GetById(blueprintId)
			);
			return result;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface IFetchBlueprintsParameters {
	pageNumber?: number;
	supplierFilterId?: number;
	locationFilterIds?: number[];
	searchTerm?: string;
	countPerPage: number;
	customerId?: number;
}

export const fetchBlueprints = createAsyncThunk<
	Hub.ResultSet<Hub.BlueprintListing>,
	IFetchBlueprintsParameters,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'blueprints/fetchBlueprints',
	async (params: IFetchBlueprintsParameters, { rejectWithValue }) => {
		const {
			pageNumber,
			supplierFilterId,
			locationFilterIds,
			searchTerm,
			countPerPage,
		} = params;

		try {
			const result = await unwrapFetch2(
				Hub.BlueprintControllerV3.GetByPost({
					pageSize: countPerPage,
					pageIndex: pageNumber,
					supplierId: supplierFilterId,
					searchTerm,
					locationIds: locationFilterIds,
				})
			);
			return result;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface BlueprintSliceDeleteBlueprintParams {
	blueprintId: number;
	successMessage: IToastMessage;
	failureMessage: IToastMessage;
}

export const deleteBlueprint = createAsyncThunk<
	number,
	BlueprintSliceDeleteBlueprintParams,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'blueprints/deleteBlueprint',
	async (params: BlueprintSliceDeleteBlueprintParams, { rejectWithValue }) => {
		try {
			await unwrapFetch2(
				Hub.BlueprintController.DeleteBlueprint(params.blueprintId)
			);

			toastr.success(params.successMessage);
			return params.blueprintId;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				toastr.error(params.failureMessage);
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				toastr.error(params.failureMessage);
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const blueprintsSlice = createSlice({
	name: 'blueprints',
	initialState: blueprintsAdapter.getInitialState(),
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchBlueprint.fulfilled, (state, action) => {
			blueprintsAdapter.upsertOne(state, action.payload);
		});
		builder.addCase(fetchBlueprints.fulfilled, (state, action) => {
			const blueprints: Domain.IBlueprint[] = action.payload.results.map(
				(x) => {
					return {
						...x,
						distributionCenterCount: 0,
						supplierProvidesRTA: false,
					};
				}
			);

			blueprintsAdapter.upsertMany(state, blueprints);
		});
		builder.addCase(deleteBlueprint.fulfilled, (state, action) => {
			blueprintsAdapter.removeOne(state, action.payload);
		});
	},
});

export function selectBlueprintById(state: IAppState, id: number) {
	return state.blueprints.entities[id];
}

export function selectBlueprintsById(
	state,
	ids: number[]
): Domain.IBlueprint[] {
	return ids.map((id) => selectBlueprintById(state, id));
}
